import { Series } from "@models/Series";
import { CT_ANALYSIS_TYPE_NAMES, EMPTY_ANALYSIS_TYPE_NAMES, SEGMENTATION_CONTROLLER_NAMES, THICKNESS_CONTROLLER_NAMES } from "./labels";
import { PanelComponentKeyType, PanelConfig, PanelType } from "./panelConfig";
import { LayoutConfig } from "./layoutConfig";
import { ToolGroupType } from "./toolsConfig";
import { ToolStateV2 } from "@features/analysis/AnalysisPageSlice";
import { configStore } from "./configStore";
import { hexToRgb } from "./colorUtils";

export type AnalysisType =  keyof typeof CT_ANALYSIS_TYPE_NAMES | keyof typeof EMPTY_ANALYSIS_TYPE_NAMES;

export type ThicknessMethodType = keyof typeof THICKNESS_CONTROLLER_NAMES.methods// 'Average'|'MIP'|'MinIP'
export type PresetType = {
  label:string;
  windowWidth:number;
  windowLevel:number;
}

export type DirectionType = [string, string, string, string];

export type SegmentationControllerType = keyof typeof SEGMENTATION_CONTROLLER_NAMES;

// interface PointInfo{
//   index : number,
//   position: [number,number,number],
//   normal: [number,number,number],
// }
export interface PickPoints {
  position?: [number,number,number],
  normal?: [number,number,number],
  viewNormal?: [number,number,number],
  index : number,
}

export interface ChamberViewProps extends PickPoints{
  spacing? : number,
  imageSize: number,
  rollRad: number,
  guideRollRad: number,
  scoutDisplayRollRad?: number,
  flipVertical:boolean,
  flipHorizontal:boolean,
  flipDirection:boolean,
  totalSpacing?: number,
  additionalSlice?: number,
  centers? : [number,number,number][],

  guideDelta? : [number,number,number],
  guidePlaneNormal? : [number,number,number],
  guideUp? : [number,number,number],

  alignAxis?:[number,number,number],
  // alignedPlaneNormal? : [number,number,number],
  // alignedUp? : [number,number,number],
}

// 없을 때 사용할 기본값들
export const defaultSliceThickness = 0.2;
export const defaultWindowCenter = 600;
export const defaultWindowWidth = 800;

export const defaultZoom: Partial<Record<
  PanelComponentKeyType,
  { mouseWheel: number; mouseMove: number }
>> = {
  VR: {
    mouseWheel: 0.05,
    mouseMove: 0.01,
  },
  Surface3D: {
    mouseWheel: 0.05,
    mouseMove: 0.01,
  },
  CPR: {
    mouseWheel: 0.05,
    mouseMove: 0.02,
  },
  CrossSection: {
    mouseWheel: 0.1,
    mouseMove: 0.04,
  },
};


export const defaultPreset : Record<string,PresetType>={
  default: {
    label:'Default',
    windowWidth: defaultWindowWidth,
    windowLevel: defaultWindowCenter,
  },
  fullDynamic: {
    label:'FullDynamic',
    windowWidth:0,
    windowLevel:0
  },
  abdomen: {
    label:'Abdomen',
    windowWidth:400,
    windowLevel:60
  },
  angio: {
    label:'Angio',
    windowWidth:600,
    windowLevel:300
  },
  bone: {
    label:'Bone',
    windowWidth:1500,
    windowLevel:300
  },
  chest: {
    label:'Chest',
    windowWidth:80,
    windowLevel:40
  },
  lungs: {
    label:'Lungs',
    windowWidth:400,
    windowLevel:40
  },
  negative: {
    label:'Negative',
    windowWidth:1500,
    windowLevel:400
  },
  chamber: {
    label:'Chamber',
    windowWidth:800,
    windowLevel:100
  }
};





export interface SegmentationProps{
  color : [number,number,number];
  // ambientColor?:[number,number,number];
  // diffuseColor?:[number,number,number];
  // specularColor?:[number,number,number];
  // specularPower?: number;
  opacity : number;
  visibility : boolean;
  // bitMask: number;
  // active : boolean;
}

export type Segmentation = Record<SegmentationControllerType, SegmentationProps>;

const DEFAULT_COLORS = {
  la: [245, 130, 31],
  lv: [224, 58, 62],
  ra: [150, 61, 151],
  rv: [0, 157, 220],
  laWall: [184, 97, 24],
  lvWall: [161, 42, 45],
  raWall: [92, 37, 92],
  rvWall: [0, 91, 136],
  aorta: [97, 187, 70],
  coronary: [255, 204, 204],
  calcium: [255, 255, 255],
  epicardial: [227, 225, 149],
  avc: [255, 0, 0],
  lowAttenuation: [255, 0, 0],
  nonCalcified: [255, 0, 0],
  calcified: [255, 0, 0],
} as const;

const getColor = (key:keyof typeof DEFAULT_COLORS) => {
  const hex = configStore.getColors(key);
  const fallback = DEFAULT_COLORS[key];
  if (!hex) return fallback;
  try {
    return hexToRgb(hex);
  } catch {
    return fallback;
  }
};

const HIDDEN_SEGMENTS = new Set<keyof typeof DEFAULT_COLORS>(['epicardial']);

export const defaultSegmentationProps: Segmentation = Object.fromEntries(
  (Object.keys(DEFAULT_COLORS) as (keyof typeof DEFAULT_COLORS)[]).map(
    (key) => [
      key,
      {
        color: getColor(key),
        opacity: HIDDEN_SEGMENTS.has(key) ? 0 : 1,
        visibility: !HIDDEN_SEGMENTS.has(key),
      },
    ],
  ),
) as Segmentation;

// AnalysisList의 인터페이스
export interface SelectedAnalysis {
  seriesId: Series['id'];
  analysisType?: AnalysisType;
}

/**
 * AnalysisTool의 인터페이스
 * *  id:@type {string}
 * *  isExclusive?:@type {boolean}
 * *  isToggleable?:@type {boolean}
 * *  groupName:@type {ToolGroupType}
 * */
export interface SelectedTool {
  id: string;
  isExclusive?: boolean;
  isToggleable?: boolean;
  groupName: ToolGroupType;
}

// // AnalysisLayout의 레이아웃 선택 인터페이스
// export interface SelectedLayout {
//   layoutId: string;
// }

// // AnalysisLayout의 패널선택 인터페이스
// export interface SelectedPanel {
//   panelConfig: PanelConfig;
// }

/**
 * // Tool 상태 -> Panel
 */
export interface ToolState {
  /**실행 후 소멸됨 */
  selectedTool: SelectedTool | null;
  /**on/off 상태 */
  activatedTools: Record<string,boolean>;
  /**한개만 활성 툴 */
  exclusiveTool: SelectedTool;
  // /**마지막 사용한 (툴 그룹: 툴) */
  // recentlyUsedTool: Record<ToolGroupType, string>;
}


/**
 * 패널 내부에서의 상태들
 */
export interface PanelState {
  /** 두께조절의 방식 */
  thicknessMethod? : ThicknessMethodType;
  /** 두께는? */
  thickness? : number;
  /** 확대 얼마나 */
  zoom?:number;
  // windowWidth?: number;
  // windowLevel?: number;

  // 인덱스좀 알아놔야겠다.
  sliceIndex?: number;


  // 정진 Shit 모음
  segmentation? : Segmentation;
  // spacing? : number;
  direction? : DirectionType;
  position? : [number, number, number];

  chamberViewProps? : ChamberViewProps;

  coronary? : {
    current : [number,number,number]; // 많아야 3개인 CPR
    currentCPRId: number; //3개중 선택된 CPR Id
    // 이것은 CPR에 매달린 그 MarkLine들.
    // 각 MarkLine은 CrossSection들을 하나씩 뿌려줘야함.
    // 0,1,2,...19 branch
    // 49 -> aorta
    state : Record<number, PickPoints[]>;
  };
  // coronary? : PickPoints[];
}

/** 선택된 패널 타입 마다 툴 저장하기 위해 */
export type ToolStates = Record<PanelType, ToolState>;
/** 선택된 패널 컴포넌트 키 마다 패널 내부 상태 저장하기 위해 */
export type PanelStates = Record<PanelComponentKeyType, PanelState>;

/** 전역적으로 온오프 딸깍될 수 있는 친구들 */
export type SwitchConfigs =
  |'switchAnnotation'
  |'switchMeasurement'
  |'switchPatientData'
  |'switchDICOMTag'
  |'switchCine'
  |'switchAxes'
  |'switchLockAxisCenter'
  |'switchSynchronizingPanel'
  |'switchClippingPlane'
  |'switchGrid'
  |'switchSegmentManagerTools'
  // |'switchCTNegativeIntensity'
  |'switchViewReport';

/** 전역적으로 온오프 딸깍 */
export type SwitchConfigType={
  [annotation in SwitchConfigs] : boolean;
}

/** 현재 레이아웃의 툴 상태이구요 */
export interface LayoutState {
  /**상단바에서 표시 가능한 레이아웃 */
  layoutConfig: LayoutConfig[];
  /**활성화된 패널과 패널 컴포넌트 키  */
  selectedPanelConfig?: PanelConfig;
  fullscreenPanelComponentKey?: PanelComponentKeyType;
  // syncPanelComponentKey?: PanelComponentKeyType;
  switchConfigs:SwitchConfigType;
  updateData: boolean;
  loading: boolean;
  error?: string | null;
}

export type VOI = {
  windowWidth:number;
  windowLevel:number;
  invert?:boolean
}
/**
  * AnalysisUIConfig 인터페이스
 * *  selectedLayoutConfig:@type {LayoutConfig} 선택한 레이아웃
 * *  layoutState?:@type {LayoutState}  ui들
 */
export interface AnalysisUIConfig {
  selectedLayoutConfig: LayoutConfig;
  layoutState: LayoutState;

  voi : VOI
  preset : Record<string,PresetType>
  // dirty:boolean;
}

// 그래픽스 상태인데, 필요없을듯
export interface CornerstoneConfig {
  renderingEngineId:string[];
  initialized : boolean;
  error? : string | null;
}

export type AnalysisDefault = VOI & {
  // windowLevel:number;
  // windowWidth:number;
}

type AnalysisDefaults = Partial<Record<AnalysisType, AnalysisDefault>>;
/**
 * Analysis 전체 상태
 */
export interface AnalysisState {
  /** 리스트에서 선택한 시리즈와 분석종류 */
  selectedAnalysis: SelectedAnalysis | null;
  /** 현재 표현될 UI에 대한 설정 */
  uiConfig: AnalysisUIConfig;
  /** 기록용 도구 상태들 */
  toolStatesV2 : ToolStateV2;
  /** 기록용 도구 상태들 */
  toolStates : ToolStates;
  /** 기록용 패널 상태들 */
  panelStates: PanelStates;
  /** 그래픽스 설정 */
  cornerstoneConfig : CornerstoneConfig;
  /** 임시로 둔 any타입 */
  analysisDefaults: AnalysisDefaults;
  /** 에러가 나오나요? */
  error?: string;
}


export function getVolumeNameByAnalysisType(
  analysisType: AnalysisType
): string | undefined {
  switch (analysisType) {
    case 'isr':
    case 'fai':
    case 'plaque':
    case 'stenosis':
    case 'calciumScore':
    case 'tavi':
      return 'volume'
    default:
      return 'volume';
  }
}

export function getCrosshairsAnalysisType(
  analysisType: AnalysisType
): boolean {
  switch (analysisType) {
    case 'MPR':
    case 'segmentation':
    case 'calciumScore':
    case 'wallthickness':
    case 'ejectionFraction':
    case 'myocardial':
      return true;
    default:
      return false;
  }
}

export function singleCrosshairsAnalysisType(
  analysisType: AnalysisType,
): boolean {
  switch (analysisType) {
    case 'fai':
    case 'plaque':
    case 'aorticValveCalcium':
    case 'stenosis':
    case 'isr':
    case 'FFR':
      return true;
    default:
      return false;
  }
}


export function getActorNameByAnalysisType(
  analysisType: AnalysisType
): string | undefined {
  switch (analysisType) {
    case 'isr':
    case 'fai':
    case 'plaque':
    case 'stenosis':
    case 'calciumScore':
      return 'coronary';
    case 'aorticValveCalcium':
    case 'tavi':
    case 'FFR':
      return 'aorta';
    case 'wallthickness':
      return 'laWall';
    default:
      return undefined;
  }
}
export const segmentManagerLabelOrder : SegmentationControllerType[]=[
  'aorta', 'coronary',
  'laWall', 'lvWall', 'raWall', 'rvWall',
  'la','lv', 'ra', 'rv',
  'epicardial','calcium'
]

export function getOffSegmentation(
  mode: string,
): (keyof Segmentation)[] {
  switch (mode) {
    case 'ventricle':
      return ['la', 'laWall', 'ra', 'raWall', 'epicardial'];
    case 'coronaryAorta':
      return [
        'lv',
        'lvWall',
        'rv',
        'rvWall',
        'la',
        'laWall',
        'ra',
        'raWall',
        'epicardial',
      ];
    case 'la':
      return [
        'lv',
        'lvWall',
        'rv',
        'rvWall',
        'laWall',
        'ra',
        'raWall',
        'epicardial',
        'aorta',
        'coronary',
        'calcium'
      ];
    default:
      return [];
  }
}

export function getSegmentationAnalysisType(
  analysisType: AnalysisType
): (keyof Segmentation)[] {
  switch (analysisType) {
    case 'segmentation':
      return [
        'aorta',
        'coronary',
        'laWall',
        'lvWall',
        'raWall',
        'rvWall',
        'la',
        'lv',
        'ra',
        'rv',
        'epicardial',
      ];
    case 'wallthickness':
      return ['laWall'];
    case 'plaque':
      return [
        'la',
        'lv',
        'ra',
        'rv',
        'laWall',
        'lvWall',
        'raWall',
        'rvWall',
        'aorta',
        'coronary',
        'epicardial',
        'calcium',
        // 'lowAttenuation','nonCalcified','calcified',
        //seg 업데이트하고 들어가는걸로
      ];
    case 'fai':
      return ['aorta', 'coronary'];
    case 'isr':
      return ['aorta', 'coronary'];
    case 'stenosis':
      return ['aorta', 'coronary'];
    case 'FFR':
      return ['aorta','coronary'];
    case 'aorticValveCalcium':
      return ['aorta', 'avc'];
    case 'tavi':
      return ['aorta'];
    case 'calciumScore':
      return ['coronary', 'calcium'];
    case 'myocardial':
      return ['aorta', 'coronary', 'lvWall', 'calcium'];
    case 'ejectionFraction':
      return ['lv'];
    case 'chamber':
    default:
      return [];
  }
}


export function getSegShapeAnalysisType(analysisType: AnalysisType){
  return [
    'segmentation',
    'plaque',
  ].includes(analysisType);
}

export function getModificationAnalysisType(analysisType: AnalysisType){
  return [
    //'segmentation',
    'FFR',
  ].includes(analysisType);
}

export function getRefreshAnalysisType(analysisType: AnalysisType) {
  return ['segmentation', 'plaque'].includes(analysisType);
}

export function getPickingVolumeType(analysisType: AnalysisType) {
  return [
    'MPR',
    'segmentation',
    'plaque',
    'fai',
    'isr',
    'stenosis',
    'ejectionFraction',
    'chamber',
    'tavi',
  ].includes(analysisType);
}

export function getSegVolumeType(analysisType: AnalysisType) {
  return [
    'segmentation',
    'chamber',
    'plaque',
    'fai',
    'isr',
    'stenosis',
    'tavi',
  ].includes(analysisType);
}

export function isVRSurfaceSwitchType(analysisType: AnalysisType) {
   return [
     'segmentation',
     'plaque',
     'stenosis',
     'fai',
     'isr',
     'tavi',
   ].includes(analysisType);
}

export function getCenterLineDisplayType(analysisType: AnalysisType) {
  return [
    'calciumScore',
    'plaque',
    'fai',
    'isr',
    'stenosis',
    'tavi',
  ].includes(analysisType);
}

export function getPlaqueDisplayType(key:string){
  return [
   'lowAttenuation',
   'calcified',
   'nonCalcified',
  ].includes(key);
}

export const EditNames = {
  centerline: 'centerline',
  contour: 'contour',
  regulator: 'regulator',
  lumen: 'lumen',
  plaque: 'plaque',
} as const;

export type EditTarget =
  | keyof typeof EditNames
  | null;
